import React from 'react'
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import { CardActions } from '@mui/material';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import FolderIcon from '@mui/icons-material/Folder';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Button } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useSelector } from 'react-redux';
import {Divider} from '@mui/material';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import AddchartIcon from '@mui/icons-material/Addchart';
import SmallChart from './SmallChart';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import GroupIcon from '@mui/icons-material/Group';

const Traders = () => {

    const formatPrice = (amount)=> new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencySign: 'standard' }).format(amount);

    const user = useSelector((state) => state.user.user);
    const list = useSelector((state) => state.traders.traders);
    const appMode = useSelector((state) => state.mode.mode);
    
     const calcPercentage = (profit, loss) => {
        let total = parseFloat(profit) + parseFloat(loss);
        let percent = profit/total;

        return percent * 100;

     }

     const isInvesting = (copiers) => {
        let status = false;
        copiers.forEach(copier => {
            if (parseInt(copier.user_id) === parseInt(user.id)) {
                status = true;
            }
        });

        return status;
     }

     const formatInvestors = (count) => {
        count = parseInt(count);
        if (count >= 1000000) {
            return (count / 1000000).toFixed(1) + 'M';
        } else if (count >= 1000) {
            return (count / 1000).toFixed(1) + 'K';
        }
        return count;
    };

    const getRiskColor = (riskScore) => {
        const score = parseInt(riskScore);
        if (score <= 3) return '#4caf50'; // Low risk - Green
        if (score <= 5) return '#ff9800'; // Medium risk - Orange
        return '#f44336'; // High risk - Red
    };

    const getRiskLabel = (riskScore) => {
        const score = parseInt(riskScore);
        if (score <= 3) return 'Low Risk';
        if (score <= 5) return 'Medium Risk';
        return 'High Risk';
    };

    // Transform traders data to investment opportunities format (same as dashboard)
    const investmentOpportunities = list.map(trader => ({
        id: trader.id,
        title: trader.trader_name,
        description: trader.description || `Professional ${trader.level} investment manager with proven track record`,
        targetAmount: parseFloat(trader.min_amount) * 10, // Estimate target amount
        raisedAmount: parseFloat(trader.min_amount) * 5, // Estimate raised amount
        expectedReturn: `${trader.gain}%`,
        duration: trader.duration,
        riskLevel: trader.risk_score <= 3 ? 'Low' : trader.risk_score <= 5 ? 'Medium' : 'High',
        category: trader.category || 'Investment Management',
        trader: trader
    }));

  return (
    <div style={{ marginTop: '75px' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
            CROWD FUNDING
        </Typography>
        
        {list.length > 0 ? (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Grid container spacing={2}>
                    {investmentOpportunities.map((opportunity) => (
                        <Grid item xs={12} md={4} key={opportunity.id}>
                            <Card sx={{ 
                                height: '100%',
                                border: '1px solid #e0e0e0',
                                '&:hover': {
                                    boxShadow: 3,
                                    transform: 'translateY(-1px)',
                                    transition: 'all 0.3s ease'
                                }
                            }}>
                                <CardContent>
                                    <Box sx={{ mb: 2 }}>
                                        <Chip 
                                            label={opportunity.category} 
                                            size="small" 
                                            sx={{ mb: 1 }}
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {opportunity.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {opportunity.description}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption">
                                                Progress: {formatPrice(opportunity.raisedAmount)} / {formatPrice(opportunity.targetAmount)}
                                            </Typography>
                                            <Typography variant="caption">
                                                {Math.round((opportunity.raisedAmount / opportunity.targetAmount) * 100)}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={(opportunity.raisedAmount / opportunity.targetAmount) * 100}
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Box>

                                    <Grid container spacing={1} sx={{ mb: 2 }}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Expected Return</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                                {opportunity.expectedReturn}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Duration</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {opportunity.duration}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Risk Level</Typography>
                                            <Chip 
                                                label={opportunity.riskLevel} 
                                                size="small"
                                                sx={{ 
                                                    bgcolor: getRiskColor(opportunity.trader.risk_score),
                                                    color: '#fff',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.7rem'
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Min Investment</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {formatPrice(opportunity.trader.min_amount)}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Button 
                                        variant="contained" 
                                        fullWidth 
                                        sx={{ mt: 1, textTransform: 'none' }}
                                        component={RouterLink}
                                        to={`/view-trades/${opportunity.id}`}
                                    >
                                        Invest Now
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
        ) : (
            <Box sx={{ 
                textAlign: 'center', 
                marginTop: '100px',
                padding: '40px 20px'
            }}>
                <BusinessCenterIcon sx={{ 
                    fontSize: 64, 
                    color: 'text.secondary',
                    marginBottom: '16px'
                }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Investment Opportunities Available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Check back later for new crowd funding opportunities
                </Typography>
            </Box>
        )}
    </div>
  )
}

export default Traders
